import { useState } from "react";
import { StyledCmSheetFooterLeftContainer } from "./CmSheetFooterLeft.styled";
import {
  CmSectionsTooltip,
  CmTooltipSection,
} from "../CmSectionsTooltip/CmSectionsTooltip";
import {
  CM_SHEET_CONFIRMED_LABEL,
  COLLABORATOR_COMMENT_LABEL,
  DEVELOPMENT_TITLE_LABEL,
  FINAL_INTERVIEW_LABEL,
  FORM_SENDING_CONFIRM_MESSAGE,
  FORM_SENDING_LABEL,
  GOALS_TOOLTIP_2_LABEL,
  GOALS_TOOLTIP_LABEL,
  HAVING_VISION_LABEL,
  INTERIM_TOOLTIP_MESSAGE,
  INTERNAL_GRADE_LABEL,
  PROFESSIONAL_PATH_LABEL,
  SEND_FORM,
  SOFT_SKILLS_LABEL,
  TOOLTIP_MESSAGE,
  WAITING_CAREER_PERFORMANCE_COMMITTEE_LABEL,
  WAITING_FOR_VISION_LABEL,
} from "../../../../../../constants/Dictionary";
import { IconManager } from "../../../../../../components/core/IconManager/IconManager";
import { IconSizeEnum } from "../../../../../../types/AppTypes";
import { theme } from "../../../../../../constants/StyledComponents";
import { ReactComponent as SendIcon } from "../../../../../../icons/send.svg";
import { ReactComponent as InfoIcon } from "../../../../../../icons/info.svg";
import { ReactComponent as HourGlassIcon } from "../../../../../../icons/hourglass.svg";
import { ReactComponent as CheckIcon } from "../../../../../../icons/check.svg";
import {
  CmSheetStateMachine,
  CollaboratorCmSheet,
} from "../../../../../../types/api/collaborators.type";
import { Tooltip } from "../../../../../../components/core/Tooltip/Tooltip";
import { PopupConfirm } from "../../../../../../components/PopupConfirm/PopupConfirm";
import { Link } from "../../../../../../components/core/Link/Link";
import {
  CmSheetEventEnum,
  CmSheetStateEnum,
  PermissionsEnum,
} from "../../../../../../constants/AppConstants";
import MessageChip from "../../../../../../components/MessageChip/MessageChip";
import { useAppSelector } from "../../../../../../hooks/store.hook";
import { useUpdateCmSheetStateMachine } from "../../../../../../hooks/cmSheet/stateMachine.hook";
import { roleHasPermission } from "../../../../../../utils/roleAuth.utils";

interface CmSheetFooterLeftProps {
  cmSheetInfo: CollaboratorCmSheet;
  cmSheetStateMachineInfo: CmSheetStateMachine;
  isInterim: boolean;
  isMyProfile?: boolean;
}

export const CmSheetFooterLeft = ({
  cmSheetInfo,
  cmSheetStateMachineInfo,
  isInterim,
  isMyProfile,
}: CmSheetFooterLeftProps) => {
  const { profile } = useAppSelector((state) => state.user);
  const [showPopup, setShowPopup] = useState<boolean>(false);

  const getFooterTooltipSection = (): CmTooltipSection[] => [
    {
      index: 1,
      label: INTERNAL_GRADE_LABEL,
      isComplete:
        cmSheetStateMachineInfo.sendCMSheetValidationSection.internalGrade ??
        false,
    },
    {
      index: 2,
      label: SOFT_SKILLS_LABEL,
      isComplete:
        cmSheetStateMachineInfo.sendCMSheetValidationSection.softSkills ??
        false,
    },
    {
      index: 3,
      label: PROFESSIONAL_PATH_LABEL,
      isComplete:
        cmSheetStateMachineInfo.sendCMSheetValidationSection.careerPath ??
        false,
    },
    {
      index: 4,
      label:
        cmSheetStateMachineInfo.currentState === CmSheetStateEnum.FINAL_FEEDBACK
          ? GOALS_TOOLTIP_2_LABEL
          : GOALS_TOOLTIP_LABEL,
      isComplete:
        cmSheetStateMachineInfo.sendCMSheetValidationSection.goals ?? false,
    },
    ...([
      CmSheetStateEnum.INTERMEDIATE_EVALUATION,
      CmSheetStateEnum.FINAL_FEEDBACK,
    ].includes(cmSheetStateMachineInfo.currentState)
      ? [
          {
            index: 5,
            label: DEVELOPMENT_TITLE_LABEL,
            isComplete:
              cmSheetStateMachineInfo.sendCMSheetValidationSection
                .development ?? false,
          },
        ]
      : []),
    ...([CmSheetStateEnum.FINAL_FEEDBACK].includes(
      cmSheetStateMachineInfo.currentState,
    )
      ? [
          {
            index: 7,
            label: COLLABORATOR_COMMENT_LABEL,
            isComplete:
              cmSheetStateMachineInfo.sendCMSheetValidationSection
                .collaboratorFeedback ?? false,
          },
        ]
      : []),
    ...([CmSheetStateEnum.FINAL_FEEDBACK].includes(
      cmSheetStateMachineInfo.currentState,
    )
      ? [
          {
            index: 8,
            label: FINAL_INTERVIEW_LABEL,
            isComplete:
              cmSheetStateMachineInfo.sendCMSheetValidationSection
                .finalInterview ?? false,
          },
        ]
      : []),
  ];

  const TooltipBody = (
    <CmSectionsTooltip
      message={isInterim ? INTERIM_TOOLTIP_MESSAGE : TOOLTIP_MESSAGE}
      sections={getFooterTooltipSection()}
    />
  );

  const { updateCmSheetStateMachine, isUpdating } =
    useUpdateCmSheetStateMachine(cmSheetInfo.id);

  const handleConfirm = async () => {
    await updateCmSheetStateMachine({
      event: CmSheetEventEnum.RICHIEDI_PRESA_VISIONE,
    });
    setShowPopup(false);
  };

  const isSendSheetEnabled = () => {
    if (
      cmSheetStateMachineInfo.currentState === CmSheetStateEnum.DRAFT &&
      cmSheetStateMachineInfo.sendCMSheetValidationSection.internalGrade &&
      cmSheetStateMachineInfo.sendCMSheetValidationSection.softSkills &&
      cmSheetStateMachineInfo.sendCMSheetValidationSection.goals &&
      cmSheetStateMachineInfo.sendCMSheetValidationSection.careerPath
    ) {
      return true;
    }

    if (
      cmSheetStateMachineInfo.currentState ===
        CmSheetStateEnum.INTERMEDIATE_EVALUATION &&
      cmSheetStateMachineInfo.sendCMSheetValidationSection.internalGrade &&
      cmSheetStateMachineInfo.sendCMSheetValidationSection.softSkills &&
      cmSheetStateMachineInfo.sendCMSheetValidationSection.goals &&
      cmSheetStateMachineInfo.sendCMSheetValidationSection.careerPath &&
      cmSheetStateMachineInfo.sendCMSheetValidationSection.development
    ) {
      return true;
    }

    if (
      cmSheetStateMachineInfo.currentState ===
        CmSheetStateEnum.FINAL_FEEDBACK &&
      cmSheetStateMachineInfo.sendCMSheetValidationSection.internalGrade &&
      cmSheetStateMachineInfo.sendCMSheetValidationSection.softSkills &&
      cmSheetStateMachineInfo.sendCMSheetValidationSection.goals &&
      cmSheetStateMachineInfo.sendCMSheetValidationSection.careerPath &&
      cmSheetStateMachineInfo.sendCMSheetValidationSection
        .collaboratorFeedback &&
      cmSheetStateMachineInfo.sendCMSheetValidationSection.finalInterview &&
      cmSheetStateMachineInfo.sendCMSheetValidationSection.development
    ) {
      return true;
    }

    return false;
  };

  const isWaitingForVision = () => {
    return (
      !isMyProfile &&
      [
        CmSheetStateEnum.DRAFT,
        CmSheetStateEnum.INTERMEDIATE_EVALUATION,
        CmSheetStateEnum.FINAL_FEEDBACK,
      ].includes(cmSheetStateMachineInfo.currentState) &&
      cmSheetStateMachineInfo.isRevisionRequestSent &&
      !cmSheetStateMachineInfo.isRevisionConfirmed
    );
  };

  const isVisionCompleted = () => {
    return (
      !isMyProfile &&
      [
        CmSheetStateEnum.DRAFT,
        CmSheetStateEnum.INTERMEDIATE_EVALUATION,
        CmSheetStateEnum.FINAL_CALIBRATION,
        CmSheetStateEnum.FINAL_FEEDBACK,
      ].includes(cmSheetStateMachineInfo.currentState) &&
      cmSheetStateMachineInfo.isRevisionRequestSent &&
      cmSheetStateMachineInfo.isRevisionConfirmed
    );
  };

  const isToBeSent = () => {
    if (!isMyProfile && !cmSheetStateMachineInfo.isRevisionRequestSent) {
      if (
        roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS) &&
        [
          CmSheetStateEnum.DRAFT,
          CmSheetStateEnum.INTERMEDIATE_EVALUATION,
          CmSheetStateEnum.FINAL_FEEDBACK,
        ].includes(cmSheetStateMachineInfo.currentState)
      ) {
        return true;
      }

      if (
        roleHasPermission(profile, PermissionsEnum.EDIT_OWN_CM_SHEET) &&
        profile.id === cmSheetInfo.careerManagerId &&
        [
          CmSheetStateEnum.DRAFT,
          CmSheetStateEnum.INTERMEDIATE_EVALUATION,
          CmSheetStateEnum.FINAL_FEEDBACK,
        ].includes(cmSheetStateMachineInfo.currentState)
      ) {
        return true;
      }

      if (
        roleHasPermission(
          profile,
          PermissionsEnum.EDIT_PROFESSIONAL_PATH_CM_SHEET,
        ) &&
        [CmSheetStateEnum.INTERMEDIATE_EVALUATION].includes(
          cmSheetStateMachineInfo.currentState,
        )
      ) {
        return true;
      }
    }

    return false;
  };

  const infoIconVisible = () => {
    if (!isMyProfile) {
      if (
        roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS) &&
        [
          CmSheetStateEnum.DRAFT,
          CmSheetStateEnum.INTERMEDIATE_EVALUATION,
          CmSheetStateEnum.FINAL_FEEDBACK,
        ].includes(cmSheetStateMachineInfo.currentState)
      ) {
        return true;
      }

      if (
        roleHasPermission(profile, PermissionsEnum.EDIT_OWN_CM_SHEET) &&
        profile.id === cmSheetInfo.careerManagerId &&
        [
          CmSheetStateEnum.DRAFT,
          CmSheetStateEnum.INTERMEDIATE_EVALUATION,
          CmSheetStateEnum.FINAL_FEEDBACK,
        ].includes(cmSheetStateMachineInfo.currentState)
      ) {
        return true;
      }

      if (
        roleHasPermission(
          profile,
          PermissionsEnum.EDIT_PROFESSIONAL_PATH_CM_SHEET,
        ) &&
        [CmSheetStateEnum.INTERMEDIATE_EVALUATION].includes(
          cmSheetStateMachineInfo.currentState,
        )
      ) {
        return true;
      }
    }

    return false;
  };

  const isVisibleCalibrationMessage = () => {
    if (
      cmSheetStateMachineInfo.currentState ===
      CmSheetStateEnum.FINAL_CALIBRATION
    ) {
      if (isMyProfile) {
        return true;
      }

      if (
        roleHasPermission(profile, PermissionsEnum.EDIT_OWN_CM_SHEET) &&
        profile.id === cmSheetInfo.careerManagerId
      ) {
        return true;
      }
    }

    return false;
  };

  const isCmSheetConfirmed = () => {
    return isMyProfile && cmSheetStateMachineInfo.isRevisionConfirmed;
  };

  return (
    <StyledCmSheetFooterLeftContainer>
      {isCmSheetConfirmed() ? (
        <MessageChip
          message={CM_SHEET_CONFIRMED_LABEL}
          color={theme.colors.green_2}
          icon={<CheckIcon />}
        />
      ) : null}
      {isVisibleCalibrationMessage() ? (
        <MessageChip
          message={WAITING_CAREER_PERFORMANCE_COMMITTEE_LABEL}
          color={theme.colors.yellow_2}
          icon={<HourGlassIcon />}
        />
      ) : null}
      {isInterim ? null : (
        <>
          {isVisionCompleted() ? (
            <MessageChip
              message={HAVING_VISION_LABEL}
              color={theme.colors.green_2}
              icon={<CheckIcon />}
            />
          ) : null}
          {isWaitingForVision() ? (
            <MessageChip
              message={WAITING_FOR_VISION_LABEL}
              color={theme.colors.yellow_2}
              icon={<HourGlassIcon />}
            />
          ) : null}
          {isToBeSent() ? (
            <>
              <Link
                icon={<SendIcon />}
                onClick={() => setShowPopup(true)}
                disabled={!isSendSheetEnabled()}
              >
                {SEND_FORM}
              </Link>
              <PopupConfirm
                isOpen={showPopup}
                onConfirm={handleConfirm}
                onCancel={() => setShowPopup(false)}
                label={FORM_SENDING_LABEL}
                popupText={FORM_SENDING_CONFIRM_MESSAGE}
                isLoading={isUpdating}
              />
            </>
          ) : null}
        </>
      )}
      {infoIconVisible() ? (
        <Tooltip message={TooltipBody}>
          <IconManager size={IconSizeEnum.MEDIUM} color={theme.colors.purple}>
            <InfoIcon />
          </IconManager>
        </Tooltip>
      ) : null}
    </StyledCmSheetFooterLeftContainer>
  );
};
